import { BrowserLogger } from "@ciaopeople/cp-shatsu/Logging";
import { getProbeFile } from "../ads/ima/probe/ProbeFileDataUrl.js";


export interface UnmuteInfo {
    unmutedAtStart: boolean,
    gesture: boolean,
    directInteraction: boolean,
    firstCheck?: boolean
    gainRemoved: boolean
}



export function isAnyUnmutable(ui: UnmuteInfo): boolean {
    return ui && (ui.directInteraction || ui.gesture || ui.unmutedAtStart);
}

export function isUnmutableByDirectInteraction(ui: UnmuteInfo): boolean {
    return ui && ui.directInteraction;
}

export function isUnmutableByGesture(ui: UnmuteInfo): boolean {
    return ui && ui.gesture;
}

export function isUnmutableFromStart(ui: UnmuteInfo): boolean {
    return ui && ui.unmutedAtStart;
}

export function isNotUnmutable(ui: UnmuteInfo): boolean {
    return !isAnyUnmutable(ui);
}

export function isFullyUnmutable(ui: UnmuteInfo): boolean {
    return ui && ui.directInteraction && ui.gesture && ui.unmutedAtStart;
}

export function isStronglyUnmutable(ui: UnmuteInfo): boolean {
    return isUnmutableByDirectInteraction(ui) || isUnmutableFromStart(ui);
}

export function isUnmutableForAllGestures(ui: UnmuteInfo): boolean {
    return isUnmutableByGesture(ui) && isUnmutableByDirectInteraction(ui);
}

export function firstUnumuteCheckDone(ui: UnmuteInfo): boolean {
    return ui && ui.firstCheck;
}


export function neverUnmute(ui: UnmuteInfo): boolean {
    return false;
}

/**
 * Interface for handling user gesture-based unmuting logic.
 *
 * Provides methods and properties to manage unmute status, listen for changes,
 * and handle actions after unmuting triggered by user gestures.
 */
export interface UserGestureUnmuter {

    get gainLevel(): number
    ensureGainMedia(): void
    afterUnmute(): Promise<void>
    whenIsFirstUnmutable(listner: UnmuteListener): void;
    currentUnmuteStatus(): UnmuteInfo;

    onUnmuteConditionChange(listener: UnmuteListener): void;
    offUnmuteConditionChange(listener: UnmuteListener): void;

    onFirstCheck(listener: UnmuteListener): void;
}

/**
 * List of DOM event types considered as user gestures for interaction detection.
 *
 * Includes mouse, pointer, keyboard, and touch events, as well as multi-touch gesture events.
 * These events can be used to determine when a user has interacted with the page,
 * which is useful for enabling features such as unmuting media playback.
 *
 * - Mouse events: 'click', 'contextmenu', 'auxclick', 'dblclick', 'mousedown', 'mouseup'
 * - Pointer events: 'pointerup'
 * - Keyboard events: 'keydown', 'keyup'
 * - Gesture events: 'gesturestart', 'gesturechange', 'gestureend'
 * - Touch events: 'touchstart', 'touchend', 'touchmove', 'touchcancel'
 */

const USER_GESTURES = [
    'click', 'contextmenu', 'auxclick', 'dblclick', 'mousedown',
    'mouseup', 'pointerup', 'keydown', 'keyup'
];

// task #14257
// const USER_GESTURES = [
//     'click', 'contextmenu', 'auxclick', 'dblclick', 'mousedown',
//     'mouseup', 'pointerup', 'keydown', 'keyup',
//     'gesturestart',  // Inizio gesture multi-touch
//     'gesturechange', // Cambiamento gesture
//     'gestureend',    // Fine gesture
//     'touchstart', // Dito tocca lo schermo
//     'touchend',  // Dito si alza dallo schermo
//     'touchmove',  // Dito si muove (solo se segue touchstart)
//     'touchcancel'
// ];



/**
 * A no-operation implementation of the {@link UserGestureUnmuter} interface.
 * 
 * This class provides default, inert behavior for all methods, making it suitable
 * for scenarios where gesture-based unmuting is not required or should be disabled.
 * 
 * @remarks
 * - All event listener methods are implemented as empty functions.
 * - The `currentUnmuteStatus` method always returns a static {@link UnmuteInfo} object
 *   indicating no interaction or gesture has occurred.
 * - The `gainLevel` property always returns `1`.
 * - The `afterUnmute` method resolves immediately.
 */
export class NoOpUnumuter implements UserGestureUnmuter {


    private readonly staticData: UnmuteInfo = {
        directInteraction: false,
        gesture: false,
        unmutedAtStart: false,
        firstCheck: true,
        gainRemoved: false
    };

    whenIsFirstUnmutable(listner: UnmuteListener): void {

    }
    currentUnmuteStatus(): UnmuteInfo {
        return this.staticData;

    }

    ensureGainMedia(): void {
    }

    get gainLevel(): number {
        return 1;
    }

    onUnmuteConditionChange(listener: UnmuteListener): void {

    }
    offUnmuteConditionChange(listener: UnmuteListener): void {

    }

    onFirstCheck(listener: UnmuteListener): void {
        listener(null, this.staticData);
    }

    afterUnmute(): Promise<void> {
        return Promise.resolve();
    }

}



/**
 * Represents a policy function that determines whether unmuting should occur
 * based on the provided {@link UnmuteInfo}.
 *
 * @param u - The information relevant to the unmute decision.
 * @returns `true` if unmuting is allowed according to the policy, otherwise `false`.
 */
export type UnmutePolicy = (u: UnmuteInfo) => boolean


/**
 * Represents a callback function that is invoked when an unmute event occurs.
 *
 * @param e - The event object associated with the unmute action.
 * @param ui - An object containing information about the unmute state.
 */
type UnmuteListener = (e: Event, ui: UnmuteInfo) => void

/**
 * Configuration options for the UserGestureUnmuter module.
 *
 * @property applyGainEffect - Indicates whether a gain effect should be applied when unmuting.
 * @property gainLevel - The level of gain to apply if the gain effect is enabled.
 */
type UnmuterConfig = {
    applyGainEffect: boolean
    gainLevel: number
}

/**
 * Implements the core logic for handling user gesture-based unmuting of media elements.
 * 
 * The `BaseUnmuter` class tracks user interactions and manages the unmute state of a given media element.
 * It supports detection of direct user gestures, initial unmute checks, and applies/removes gain effects
 * using the Web Audio API for volume control.
 * 
 * Key features:
 * - Listens for user gesture events to determine when the media can be unmuted.
 * - Handles probe media loading for browsers that require user interaction before playback.
 * - Manages listeners for unmute condition changes and first unmute events.
 * - Optionally applies a gain effect to the media element's audio output.
 * 
 * @remarks
 * This class is intended to be used as a base for more specialized unmuter implementations.
 * It requires configuration via the `UnmuterConfig` interface and depends on several utility functions
 * and types such as `UnmuteInfo`, `UnmuteListener`, `USER_GESTURES`, and logging via `BrowserLogger`.
 * 
 * @example
 * ```typescript
 * const unmuter = new BaseUnmuter(rootElement, mediaElement, config);
 * unmuter.onUnmuteConditionChange((evt, info) => {
 *   // Handle unmute state change
 * });
 * ```
 * 
 * @see UnmuteInfo
 * @see UnmuteListener
 * @see UnmuterConfig
 * @see BrowserLogger
 */
export class BaseUnmuter implements UserGestureUnmuter {
    private uinfo: UnmuteInfo;
    private readonly userInputEventNames = USER_GESTURES;

    private firstUnmuteListeners: UnmuteListener[];
    private onNextUnumuteListeners: UnmuteListener[];
    private firstCheckListeners: UnmuteListener[];
    private logger: BrowserLogger;

    private gainActive: boolean = false;

    constructor(
        private rootElement: HTMLElement,
        private mediaElement: HTMLMediaElement,
        private config: UnmuterConfig
    ) {
        this.uinfo = {
            directInteraction: false,
            unmutedAtStart: false,
            gesture: false,
            gainRemoved: false,
        };

        this.firstUnmuteListeners = [];
        this.onNextUnumuteListeners = [];
        this.firstCheckListeners = [];

        this.logger = new BrowserLogger("AGGRO");

        const funcUserGesture = (e: Event) => {
            setTimeout(() => {
                const oldvalues = { ...this.uinfo };
                this.uinfo.gesture = true;
                this.uinfo.directInteraction = this.rootElement.contains(e.target as HTMLElement);

                if (isUnmutableForAllGestures(this.uinfo)) {
                    this.userInputEventNames.forEach((eventName) => {
                        document.removeEventListener(eventName, funcUserGesture);
                    });
                }
                if (oldvalues.gesture !== this.uinfo.gesture || oldvalues.directInteraction !== this.uinfo.directInteraction) {
                    this.triggerNext(e);
                }
            }, 0);
        };

        const useProbeVideo = this.mediaElement.src ? false : true;
        if (useProbeVideo) {
            this.logger.log("Setting probe media");
            this.mediaElement.preload = "metadata";
            this.mediaElement.src = getProbeFile();
        }

        this.mediaElement.muted = false;
        this.mediaElement.play()
            .then(() => {
                this.mediaElement.muted = true;
                this.logger.log("Can play with audio on directly");
                this.mediaElement.pause();
                if (useProbeVideo) this.mediaElement.src = "";
                this.uinfo.unmutedAtStart = true;
                this.uinfo.firstCheck = true;
                this.triggerNext(null);
            })
            .catch(() => {
                this.mediaElement.muted = true;
                this.logger.log("Could not play audio on directly");
                this.uinfo.firstCheck = true;
                this.uinfo.unmutedAtStart = false;
                if (useProbeVideo) this.mediaElement.src = "";
                this.triggerNext(null);
            });

        this.userInputEventNames.forEach((eventName) => {
            document.addEventListener(eventName, funcUserGesture);
        });
    }


    get gainLevel(): number {

        let gainlev = 1
        if (this.config.applyGainEffect) {
            if (this.gainActive) {
                gainlev = this.config.gainLevel * this.config.gainLevel;
            } else if (this.uinfo.gainRemoved) {
                gainlev = 1;
            } else
                gainlev = 0.25;
        } else {
            gainlev = 1;
        }

        this.logger.info("CHECK gainLevel ", gainlev, this.gainActive, this.uinfo,)

        return gainlev
    }


    afterUnmute(): Promise<void> {
        this.logger.info("CHECK afterUnmute ", this.gainActive, this.uinfo,)

        if (this.config.applyGainEffect) {
            if (!this.uinfo.gainRemoved && this.gainActive) {
                this.removeGainEffect();
            } else if (!this.uinfo.gainRemoved && !this.gainActive) {
                this.eventuallyEditGain();
            }
        }


        return Promise.resolve();
    }


    ensureGainMedia(): void {
        this.logger.info("CHECK ensureGainMedia ", this.gainActive, this.uinfo,)

        this.mediaElement.volume = this.gainLevel
    }


    private eventuallyEditGain(): void {
        this.logger.info("CHECK eventuallyEditGain ", this.gainActive, this.uinfo,)
        try {
            if (!this.uinfo.gainRemoved && !this.gainActive) {

                this.gainActive = true;
                this.uinfo.gainRemoved = false;
                this.ensureGainMedia()
                this.triggerNext(null);

            }
            return

        } catch (error) {
            this.logger.warn('eventuallyEditGain:', error);
            return
        }
    }

    /**
   * Rimuove l'attenuazione riportando i gain a 1,
   * MA MANTIENE la catena audio collegata.
   */
    private removeGainEffect(): void {
        this.logger.info("CHECK removeGainEffect ", this.gainActive, this.uinfo,)
        try {
            this.gainActive = false;
            this.uinfo.gainRemoved = true;
            this.ensureGainMedia()
            this.triggerNext(null);
        } catch (error) {
            this.logger.warn('Errore rimozione gain:', error);
        }
    }



    private triggerNext(maybeEvt: Event): void {
        if (this.uinfo.firstCheck) {
            this.logger.debug("Updated uinfo", this.uinfo, maybeEvt);
            let useListeners = this.onNextUnumuteListeners;
            if (this.firstCheckListeners) {
                useListeners = useListeners.concat(this.firstCheckListeners);
                this.firstCheckListeners = null;
            }
            if (isAnyUnmutable(this.uinfo)) {
                useListeners = useListeners.concat(this.firstUnmuteListeners);
                this.firstUnmuteListeners = [];
            }

            useListeners.forEach((l) => {
                try {
                    this.logger.debug("Running listener on change");
                    l(maybeEvt, this.uinfo);
                } catch (e) {
                    this.logger.error("error running interaction listener", e);
                }
            });
            this.firstUnmuteListeners = [];

            if (isFullyUnmutable(this.uinfo)) {
                this.onNextUnumuteListeners = [];
            }
        }
    }

    onUnmuteConditionChange(listener: UnmuteListener): void {
        if (isFullyUnmutable(this.uinfo)) {
            setTimeout(() => listener(null, this.uinfo), 0);
        }
        else this.onNextUnumuteListeners.push(listener);
    }

    offUnmuteConditionChange(listener: UnmuteListener): void {
        this.onNextUnumuteListeners = this.onNextUnumuteListeners.filter(l => l != listener);
    }
    whenIsFirstUnmutable(listener: UnmuteListener): void {
        if (isAnyUnmutable(this.uinfo)) {
            setTimeout(() => listener(null, this.uinfo), 0);
        }
        else this.firstUnmuteListeners.push(listener);
    }

    currentUnmuteStatus(): UnmuteInfo {
        return this.uinfo;
    }

    onFirstCheck(listener: UnmuteListener): void {
        if (this.uinfo.firstCheck) {
            setTimeout(() => listener(null, this.uinfo), 0);
        }
        else this.firstCheckListeners.push(listener);
    }

}
